import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'
import sortBy from 'lodash/sortBy.js'
import get from 'lodash/get.js'

import {getState, setForm, updateForm, formsSelector} from '../store.js'
import apiverson from '../common/apiverson.js'
import {fetchAllApiverson} from '../common/fetchAll.js'
import {TAG_TYPE__STOCKTAKE} from '../common/constants/Tags.js'
import {showGlobalError} from '../ordoro/GlobalErrorMessage.js'
import {patchStocktakeTag, stocktakesSelector} from './stocktakes.js'
import {refreshStocktakeList} from '../ordoro/StocktakeListPage/stocktakeListFunctions.js'
import {hasUseStocktakeFeatureSelector} from '../data/company.js'
import {tokenizeOptions, getNounsFromValue} from '../common/tokenizeOptions.js'

export const STOCKTAKE_TAGS = 'stocktake_tags'

export function setStocktakeTags(stocktakeTags) {
  setForm(STOCKTAKE_TAGS, keyBy(stocktakeTags, 'id'))
}

export function updateStocktakeTag(stocktakeTag) {
  updateForm(STOCKTAKE_TAGS, {[stocktakeTag.id]: stocktakeTag})
}

export function removeStocktakeTag(stocktakeTag) {
  const stocktakeTags = {...stocktakeTagsSelector(getState())}

  delete stocktakeTags[stocktakeTag.id]

  setForm(STOCKTAKE_TAGS, stocktakeTags)
}

export function stocktakeTagsSelector(state) {
  return formsSelector(state)[STOCKTAKE_TAGS] || stocktakeTagsSelector.default
}
stocktakeTagsSelector.default = {}

export function stocktakeTagSelector(state, {stocktakeTagID}) {
  return stocktakeTagsSelector(state)[stocktakeTagID]
}

export const stocktakeTagsSortedByNameSelector = createSelector(
  stocktakeTagsSelector,
  (stocktakeTags) => sortBy(stocktakeTags, ({name}) => name.toLowerCase()),
)

export const stocktakeTagOptionsSelector = createSelector(
  stocktakeTagsSortedByNameSelector,
  (tags) =>
    tags.map((tag) => ({
      value: tag.id,
      display: tag.name,
      entity: tag,
      type: TAG_TYPE__STOCKTAKE,
      nouns: getNounsFromValue(tag.name),
    })),
)

export const stocktakeTagOptionTokensSelector = createSelector(
  stocktakeTagOptionsSelector,
  (tagOptions) => tokenizeOptions(tagOptions),
)

export function stocktakeTagsHaveLoadedSelector(state) {
  return !!formsSelector(state)[STOCKTAKE_TAGS]
}

export async function getStocktakeTags() {
  if (!hasUseStocktakeFeatureSelector(getState())) {
    return
  }

  const stocktakeTags = await fetchAllApiverson('/stocktake/tag/', 'tag')

  setStocktakeTags(stocktakeTags)
}

export async function saveStocktakeTag(name, color, id) {
  const params = {name: name.trim(), color}
  const originalTag = id
    ? stocktakeTagSelector(getState(), {stocktakeTagID: id})
    : null

  const {json} = id
    ? await apiverson.put(
        `/stocktake/tag/${encodeURIComponent(originalTag.name)}`,
        params,
      )
    : await apiverson.post('/stocktake/tag', params)

  updateStocktakeTag(json)

  if (id) {
    await refreshStocktakeList()
  }

  return json
}

export async function deleteStocktakeTag(tagID) {
  const tag = stocktakeTagSelector(getState(), {stocktakeTagID: tagID})

  await apiverson.delete(`/stocktake/tag/${encodeURIComponent(tag.name)}`)

  removeStocktakeTag(tag)
}

export async function addTagToStocktakes(stocktakeTagID, referenceIDs) {
  const stocktakes = stocktakesSelector(getState())
  const tag = stocktakeTagSelector(getState(), {stocktakeTagID})

  const referenceIDsWithoutTag = referenceIDs.filter(
    (referenceID) =>
      !get(stocktakes, [referenceID, 'tags'], []).find(
        (t) => t.id === stocktakeTagID,
      ),
  )

  referenceIDsWithoutTag.map((referenceID) =>
    patchStocktakeTag(referenceID, tag, true),
  )

  try {
    await Promise.all(
      referenceIDsWithoutTag.map((referenceID) =>
        apiverson.post(
          `/stocktake/${encodeURIComponent(
            referenceID,
          )}/tag/${encodeURIComponent(tag.name)}`,
        ),
      ),
    )
  } catch (error) {
    showGlobalError(
      {
        summary: 'Error adding Stocktake tag.',
        details: error.error_message || error.message,
      },
      error,
    )

    referenceIDsWithoutTag.map((referenceID) =>
      patchStocktakeTag(referenceID, tag, false),
    )
  }
}

export async function removeTagFromStocktakes(stocktakeTagID, referenceIDs) {
  const stocktakes = stocktakesSelector(getState())
  const tag = stocktakeTagSelector(getState(), {stocktakeTagID})

  const referenceIDsWithTag = referenceIDs.filter((referenceID) =>
    get(stocktakes, [referenceID, 'tags'], []).find(
      (t) => t.id === stocktakeTagID,
    ),
  )

  referenceIDsWithTag.map((referenceID) =>
    patchStocktakeTag(referenceID, tag, false),
  )

  try {
    await Promise.all(
      referenceIDsWithTag.map((referenceID) =>
        apiverson.delete(
          `/stocktake/${encodeURIComponent(
            referenceID,
          )}/tag/${encodeURIComponent(tag.name)}`,
        ),
      ),
    )
  } catch (error) {
    showGlobalError(
      {
        summary: 'Error removing Stocktake tag.',
        details: error.error_message || error.message,
      },
      error,
    )

    referenceIDsWithTag.map((referenceID) =>
      patchStocktakeTag(referenceID, tag, true),
    )
  }
}
